import type { _SERVICE as HelperActor } from '../idls/ethHelper/ethHelper.did'
import type { ActorSubclass } from '@dfinity/agent'
import type { SolSendParams } from '../interfaces/walletActions'

import {
  createTransactionMessage,
  setTransactionMessageFeePayerSigner,
  setTransactionMessageLifetimeUsingBlockhash,
  appendTransactionMessageInstructions,
  address,
  createSolanaRpc,
  createNoopSigner,
  pipe,
  compileTransaction,
  sendTransactionWithoutConfirmingFactory,
  getBase58Decoder,
} from '@solana/kit'
import { getTransferSolInstruction } from '@solana-program/system'
import { base64ToArrayBuffer } from '../utils/arrayBuffer'
import { fetchEnv } from '../utils/fetchEnv'
import { logger } from '../logger'

interface SignatureResponse {
  result: {
    value: {
      confirmationStatus: 'processed' | 'confirmed' | 'finalized'
    }[]
  }
}

const CLUSTER = fetchEnv('SOLANA_CLUSTER') || 'devnet'
const RPC_ENDPOINT = fetchEnv('SOLANA_RPC_ENDPOINT') || `https://api.${CLUSTER}.solana.com`

const MAX_POLL_COUNT = 40

class SolanaClient {
  rpc = createSolanaRpc(RPC_ENDPOINT)

  transactionExploreUrl(transactionId: string): string {
    return `https://explorer.solana.com/tx/${transactionId}?cluster=${CLUSTER}`
  }

  async getBalance(solAddress: string): Promise<number> {
    const response = await this.rpc.getBalance(address(solAddress)).send()

    return Number(response.value)
  }

  async checkIfTransactionConfirmed(signature: string): Promise<boolean> {
    const requestBody = {
      jsonrpc: '2.0',
      id: 1,
      method: 'getSignatureStatuses',
      params: [
        [signature],
        {
          searchTransactionHistory: true,
        },
      ],
    }

    const response = await fetch(RPC_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })

    if (response.ok) {
      const parsedResponse: SignatureResponse = await response.json()

      return parsedResponse.result.value[0]?.confirmationStatus === 'finalized'
    } else {
      return false
    }
  }

  async pollForFinalized(signature: string): Promise<void> {
    for (let retryCount = 0; retryCount < MAX_POLL_COUNT; retryCount += 1) {
      const finalised = await this.checkIfTransactionConfirmed(signature)

      if (finalised) {
        return
      } else {
        await new Promise((r) => setTimeout(r, 500))
      }
    }

    throw 'Solana: unable to receive finalized transaction state'
  }

  async sendSol(
    { from, to, amount }: SolSendParams,
    helperActor: ActorSubclass<HelperActor>
  ): Promise<void> {
    const { value: latestBlockhash } = await this.rpc.getLatestBlockhash().send()
    const fromAddress = address(from)
    const signer = createNoopSigner(fromAddress)

    const transactionMessage = pipe(
      createTransactionMessage({ version: 0 }),
      (m) => setTransactionMessageFeePayerSigner(signer, m),
      (m) => setTransactionMessageLifetimeUsingBlockhash(latestBlockhash, m),
      (m) =>
        appendTransactionMessageInstructions(
          [
            getTransferSolInstruction({
              source: signer,
              destination: address(to),
              amount: Number(amount),
            }),
          ],
          m
        )
    )

    const compiledTx = compileTransaction(transactionMessage)
    const signature = await helperActor.sign_sol([...compiledTx.messageBytes])

    if ('Ok' in signature) {
      const signedTx = {
        messageBytes: compiledTx.messageBytes,
        lifetimeConstraint: compiledTx.lifetimeConstraint,
        signatures: {
          [fromAddress]: signature.Ok,
        },
      }

      const sendTransaction = sendTransactionWithoutConfirmingFactory({
        rpc: this.rpc,
      })

      try {
        // @ts-ignore
        await sendTransaction(signedTx, {
          commitment: 'confirmed',
        })

        await this.pollForFinalized(getBase58Decoder().decode(Uint8Array.from(signature.Ok)))
      } catch (e) {
        logger.error('Solana send error', {
          originalError: e,
        })
        return Promise.reject(this.#normalizeSendError(e))
      }
    } else {
      return Promise.reject(signature.Err)
    }
  }

  async calculateAccountRent(solAddress: string): Promise<BigInt> {
    const response = await this.rpc
      .getAccountInfo(address(solAddress), { encoding: 'base64' })
      .send()

    const data = response.value?.data[0] || ''
    const accountSize = base64ToArrayBuffer(data).byteLength

    return this.rpc.getMinimumBalanceForRentExemption(BigInt(accountSize)).send()
  }

  #normalizeSendError(error: any) {
    if (error?.context?.__code === -32002) {
      return new Error(
        'The transaction seems to be invalid, make sure both accounts have enough funds to cover rent fees'
      )
    } else {
      return new Error('Random error occured, please try again or contact support')
    }
  }
}

const solanaClient = new SolanaClient()

export { solanaClient }
